import { memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNews } from '../../context/NewsContext';
import { useLang } from '../../context/LanguageContext';
import { CATEGORIES } from '../../utils/constants';
import { timeAgo } from '../../utils/formatDate';
import { getCategoryFallbackImage } from '../../utils/categoryImages';
import { isBrandedImage } from '../../utils/isBrandedImage';
import AdBanner from './AdBanner';

/* ── Trending / ट्रेंडिंग Widget ──────────────────────── */
const TrendingWidget = memo(({ articles }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const trending = articles.filter(a => a.image).slice(0, 5);

  return (
    <div className="sidebar-widget">
      <div className="sidebar-widget__title">
        <span className="sidebar-widget__bar" style={{ background: '#FFD700' }} />
        {t('trendingNow')}
      </div>
      <div className="sidebar-widget__body">
        {trending.map((article, i) => {
          const handleClick = () => navigate(`/article/${article.id}`);
          return (
            <div
              key={article.id}
              onClick={handleClick}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '8px 0',
                borderBottom: i < trending.length - 1 ? '1px solid var(--card-border)' : 'none',
                cursor: 'pointer',
              }}
            >
              <span style={{
                fontFamily: "'Mukta', 'Noto Sans Devanagari', sans-serif",
                fontSize: '1.5rem',
                fontWeight: 700,
                color: i === 0 ? '#CC0000' : 'var(--text-muted)',
                lineHeight: 1,
                flexShrink: 0,
                width: '22px',
                textAlign: 'center',
              }}>
                {i + 1}
              </span>
              <p
                style={{
                  fontFamily: "'Mukta', 'Noto Sans Devanagari', sans-serif",
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: 0,
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                {article.title}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ── Categories / श्रेणियां Widget ───────────────────── */
const CategoriesWidget = memo(() => {
  const { lang, t } = useLang();
  return (
    <div className="sidebar-widget">
      <div className="sidebar-widget__title">
        <span className="sidebar-widget__bar" />
        {t('categories')}
      </div>
      <div className="sidebar-widget__body">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '5px 11px',
                background: `${cat.color}18`,
                border: `1px solid ${cat.color}44`,
                borderRadius: '20px',
                color: 'var(--text-secondary)',
                fontSize: '11px',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${cat.color}44`; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${cat.color}18`; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <span>{cat.emoji}</span>
              <span>{lang === 'EN' ? cat.labelEn : cat.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
});

/* ── Popular News / लोकप्रिय खबरें Widget ────────────── */
const PopularWidget = memo(({ articles }) => {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const popular = articles.filter(a => a.image).slice(5, 10);

  return (
    <div className="sidebar-widget">
      <div className="sidebar-widget__title">
        <span className="sidebar-widget__bar" style={{ background: '#FF6B35' }} />
        {t('popularNews')}
      </div>
      <div className="sidebar-widget__body">
        {popular.map((article) => {
          const img = (article.image && !isBrandedImage(article.image))
            ? article.image
            : getCategoryFallbackImage(article.category, article.id, article.title, article.summary);
          const handleClick = () => navigate(`/article/${article.id}`);
          return (
            <div key={article.id} className="sidebar-news-item" onClick={handleClick}>
              <div className="sidebar-news-item__img">
                <img
                  src={img}
                  alt={article.title}
                  loading="lazy"
                  onError={(e) => { e.target.onerror = null; e.target.src = getCategoryFallbackImage(article.category, article.id, article.title, article.summary); }}
                />
              </div>
              <div className="sidebar-news-item__text">
                <p className="sidebar-news-item__headline">{article.title}</p>
                <span className="sidebar-news-item__time">{timeAgo(article.pubDate, lang.toLowerCase())}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ── Breaking Updates / ब्रेकिंग अपडेट Widget ─────────── */
const BreakingUpdatesWidget = memo(({ articles }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const items = articles.slice(15, 20);

  return (
    <div className="sidebar-widget" style={{ borderLeft: '3px solid #CC0000' }}>
      <div className="sidebar-widget__title" style={{ color: '#CC0000' }}>
        <span className="sidebar-widget__bar" style={{ background: '#CC0000' }} />
        {t('breakingUpdates')}
      </div>
      <div className="sidebar-widget__body">
        {items.map((article, i) => (
          <div
            key={article.id}
            onClick={() => navigate(`/article/${article.id}`)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              padding: '7px 0',
              borderBottom: i < items.length - 1 ? '1px solid var(--card-border)' : 'none',
              cursor: 'pointer',
            }}
          >
            <span style={{
              flexShrink: 0,
              background: '#CC0000',
              color: '#fff',
              fontSize: '8px',
              fontWeight: 800,
              padding: '2px 5px',
              borderRadius: '2px',
              letterSpacing: '0.5px',
              marginTop: '3px',
              whiteSpace: 'nowrap',
            }}>
              {t('breaking')}
            </span>
            <p
              style={{
                fontFamily: "'Mukta', 'Noto Sans Devanagari', sans-serif",
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: 0,
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            >
              {article.title}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
});

/* ── Left Sidebar ─────────────────────────────────────── */
const LeftSidebar = () => {
  const { allArticles } = useNews();
  return (
    <aside>
      <TrendingWidget articles={allArticles.slice(0, 20)} />
      <CategoriesWidget />
      <AdBanner size="300x250" index={2} />
      <PopularWidget articles={allArticles} />
      <BreakingUpdatesWidget articles={allArticles} />
    </aside>
  );
};

export default LeftSidebar;
